
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useActiveAffiliates, useTransferUserToAffiliate } from '@/hooks/useUserAssignment';
import { Loader2 } from 'lucide-react';

interface TransferReferralDialogProps {
  user: { id: string; masked_name: string; masked_email: string; current_affiliate?: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TransferReferralDialog = ({ user, open, onOpenChange }: TransferReferralDialogProps) => {
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const { data: affiliates, isLoading: loadingAffiliates } = useActiveAffiliates({ enabled: open });
  const transferMutation = useTransferUserToAffiliate();

  const handleTransfer = async () => {
    if (!user || !selectedAffiliateId) return;

    const selectedAffiliate = affiliates?.find(a => a.id === selectedAffiliateId);
    if (!selectedAffiliate) return;

    console.log('🔄 Initiating transfer:', {
      userId: user.id,
      fromAffiliate: user.current_affiliate,
      toAffiliate: selectedAffiliate.name,
      newAffiliateId: selectedAffiliateId,
      newReferralCode: selectedAffiliate.referral_code
    });

    try {
      await transferMutation.mutateAsync({
        userId: user.id,
        newAffiliateId: selectedAffiliateId,
        newReferralCode: selectedAffiliate.referral_code
      });

      console.log('✅ Transfer completed successfully');
      onOpenChange(false);
      setSelectedAffiliateId('');
      setReason('');
    } catch (error) {
      console.error('❌ Error transferring user:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Transfer User to New Affiliate</DialogTitle>
          <DialogDescription>
            Transfer {user?.masked_name} ({user?.masked_email}) to a different affiliate partner.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="affiliate" className="text-right">
              New Affiliate
            </Label>
            <div className="col-span-3">
              <Select value={selectedAffiliateId} onValueChange={setSelectedAffiliateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new affiliate" />
                </SelectTrigger>
                <SelectContent>
                  {loadingAffiliates ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : (
                    affiliates?.map((affiliate) => (
                      <SelectItem key={affiliate.id} value={affiliate.id}>
                        {affiliate.name} ({affiliate.referral_code})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="reason" className="text-right mt-2">
              Reason
            </Label>
            <div className="col-span-3">
              <Textarea
                id="reason"
                placeholder="Optional reason for transfer..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={transferMutation.isPending}>
            Cancel
          </Button>
          <Button 
            onClick={handleTransfer}
            disabled={!selectedAffiliateId || transferMutation.isPending}
          >
            {transferMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Transferring...
              </>
            ) : (
              'Transfer'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
